// framework/client/components/DataWidget.js
import { WidgetBase } from "../core/WidgetBase.js";
import { avatarHelper } from "../helpers/avatarHelper.js";
import Mustache from 'mustache';

export class DataWidget extends WidgetBase {
    static styles = `
        .data-widget-content {
            padding: 0.25rem; /* Add some padding inside the widget body */
        }
        .data-widget-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .data-widget-list-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.6rem 0.5rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            transition: background-color 0.2s ease-in-out;
        }
        .data-widget-list-item:last-child {
            border-bottom: none;
        }
        .data-widget-list-item.clickable:hover {
            background-color: rgba(var(--bs-body-color-rgb), 0.08);
            cursor: pointer;
        }
        .list-item-content {
            flex-grow: 1;
            overflow: hidden;
        }
        .list-item-title {
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .list-item-subtitle {
            font-size: var(--ab-text-sm);
            color: var(--bs-secondary-color);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .list-item-meta {
            flex-shrink: 0;
            font-size: var(--ab-text-sm);
            color: var(--bs-secondary-color);
            text-align: right;
        }
        .data-widget-no-data {
            text-align: center;
            color: var(--bs-secondary-color);
            padding: 2rem;
        }
    `;

    constructor(config) {
        if (!config.widget.size) {
            config.widget.size = 'large';
        }
        super(config);
        this.applyStyles();
    }

    _getValueByPath(obj, path) {
        if (!path || !obj) return undefined;
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    async renderContent() {
        if (this.error) {
            this.contentContainer.innerHTML = `<div class="widget-error">Error: ${this.error.message}</div>`;
            return;
        }
        if (this.isLoading) { return; }

        this.contentContainer.innerHTML = '';
        this.contentContainer.classList.add('data-widget-content');

        const mainData = this.data?.main;

        if (!mainData || !Array.isArray(mainData) || mainData.length === 0) {
            this.contentContainer.innerHTML = `<div class="data-widget-no-data">${this.t(this.config.widget.noDataMessage || 'No data available.')}</div>`;
            this.appClient.tx.applyTranslations(this.contentContainer);
            return;
        }

        // --- START OF THE FIX ---
        // Check if a custom itemTemplate function is provided in the widget config.
        const itemTemplate = this.getConfig('itemTemplate');
        if (typeof itemTemplate === 'function') {
            // If it exists, use it to render the content directly. This supports fully custom layouts.
            this.contentContainer.innerHTML = mainData.map(item => itemTemplate(item)).join('');
        } else {
            // Otherwise, fall back to the default structured list rendering.
            const itemsContainer = document.createElement("ul");
            itemsContainer.className = "data-widget-list";

            const itemPromises = mainData.map(item => this.renderItem(item));
            const itemElements = await Promise.all(itemPromises);
            itemElements.forEach(el => itemsContainer.appendChild(el));

            this.contentContainer.appendChild(itemsContainer);
        }
        // --- END OF THE FIX ---

        this.appClient.tx.applyTranslations(this.contentContainer);
    }

    async renderItem(item) {
        const itemElement = document.createElement('li');
        itemElement.className = 'data-widget-list-item';

        const itemAction = this.getConfig('itemAction');
        if (itemAction && itemAction.type === 'navigate' && itemAction.route) {
            itemElement.classList.add('clickable');
            itemElement.addEventListener('click', () => {
                const route = Mustache.render(itemAction.route, item);
                this.appClient.layoutEngine.navigate(route);
            });
        }

        const fieldsConfig = this.config.widget.fields || [];
        
        const avatarField = fieldsConfig.find(f => f.isAvatar);
        const contentFields = fieldsConfig.filter(f => !f.isAvatar);

        let avatarHtml = '';
        if (avatarField) {
            const contactId = this._getValueByPath(item, avatarField.name);
            if (contactId) {
                const contact = await this.appClient.rds.get('projectContacts', contactId);
                const avatar = avatarHelper.createAvatarElement({
                    displayName: contact?.displayName || '?',
                    avatarUrl: contact?.avatarUrl,
                    sizeClass: 'default'
                });
                avatarHtml = avatar.outerHTML;
            }
        }

        const renderField = (fieldConfig) => {
            if (!fieldConfig) return '';
            const value = this._getValueByPath(item, fieldConfig.name);
            return fieldConfig.render ? fieldConfig.render(value, item, this) : (value || '');
        };

        const titleField = contentFields.find(f => f.isTitle) || contentFields[0];
        let subtitleField, metaField;
        let otherContentHtml = '';

        if (contentFields.length === 2) {
            otherContentHtml = renderField(contentFields[1]);
        } else if (contentFields.length >= 3) {
            subtitleField = contentFields.find(f => f.isSubtitle) || contentFields[1];
            metaField = contentFields.find(f => f.isMeta) || contentFields[2];
        }

        itemElement.innerHTML = `
            ${avatarHtml}
            <div class="list-item-content">
                <div class="list-item-title">${renderField(titleField)}</div>
                ${subtitleField ? `<div class="list-item-subtitle">${renderField(subtitleField)}</div>` : ''}
                ${otherContentHtml}
            </div>
            ${metaField ? `<div class="list-item-meta">${renderField(metaField)}</div>` : ''}
        `;

        return itemElement;
    }


    capitalizeFirstLetter(string) {
        if (!string || typeof string !== 'string') return '';
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
}